import { ApiError, fillPath, getJson, sendJson } from '@/core/api/http'
import {
  detailNamesToken,
  getBlattWithToken,
  isPortalSession,
  logoutToken,
  type PortalBlatt,
  type PortalSession,
} from './portal'

export const GEGENZEICHNUNG = {
  '04.3': 'traeger',
  '04.6': 'unterweiser',
  '04.7': 'traeger',
} as const

export type GegenzeichnungBlatt = keyof typeof GEGENZEICHNUNG

export function isGegenzeichnungBlatt(value: unknown): value is GegenzeichnungBlatt {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(GEGENZEICHNUNG, value)
}

export const EINGESCHRAENKT = 'teilnehmerakte_eingeschraenkt'

export function isRefusal(error: unknown, token: string): boolean {
  if (!(error instanceof ApiError) || error.status !== 409) return false
  return detailNamesToken(error.detailText, token)
}

export function isEingeschraenkt(error: unknown): boolean {
  return isRefusal(error, EINGESCHRAENKT)
}

export type Profil = { teilnehmende: Record<string, unknown>; vergabe: Record<string, unknown> }

export async function getProfil(coachingId: string): Promise<Profil> {
  const body = await getJson<unknown>(fillPath('/vergabe-portal/coaching/{coaching_id}/profil', { coaching_id: coachingId }))
  const row = body as Partial<Profil> | null
  const isRecord = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value)
  if (!row || !isRecord(row.teilnehmende) || !isRecord(row.vergabe)) throw new Error('profil: unexpected answer')
  return { teilnehmende: row.teilnehmende, vergabe: row.vergabe }
}

export type LoeschStatus = { unterschrieben: boolean; darfLoeschen: boolean }

export async function getLoeschStatus(coachingId: string): Promise<LoeschStatus> {
  let body: { unterschrieben?: unknown; darf_loeschen?: unknown } | null
  try {
    body = await getJson(fillPath('/vergabe-portal/coaching/{coaching_id}/teilnehmerakte', { coaching_id: coachingId }))
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) throw new Error('teilnehmerakte: coaching not visible')
    throw error
  }
  if (typeof body?.unterschrieben !== 'boolean' || typeof body.darf_loeschen !== 'boolean') {
    throw new Error('teilnehmerakte: unexpected answer')
  }
  return { unterschrieben: body.unterschrieben, darfLoeschen: body.darf_loeschen }
}

export async function openTeilnehmerakte(coachingId: string): Promise<PortalSession> {
  const body = await sendJson<unknown>(
    fillPath('/vergabe-portal/coaching/{coaching_id}/teilnehmerakte/oeffnen', { coaching_id: coachingId }),
    'POST',
    {},
  )
  if (!isPortalSession(body)) throw new Error('teilnehmerakte/oeffnen: unexpected answer')
  return { token: body.token, sprache: body.sprache ?? null, blaetter: body.blaetter }
}

export function gegenzeichnen(coachingId: string, blatt: string, signaturPngBase64: string): Promise<void> {
  if (!isGegenzeichnungBlatt(blatt)) {
    return Promise.reject(new Error(`gegenzeichnung: sheet ${blatt} is not countersigned by staff`))
  }
  return sendJson<void>(
    fillPath('/vergabe-portal/coaching/{coaching_id}/teilnehmerakte/blatt/{blatt}/gegenzeichnung', {
      coaching_id: coachingId,
      blatt,
    }),
    'POST',
    { signatur_png_base64: signaturPngBase64 },
  )
}

export async function readTeilnehmerakte(coachingId: string, blaetter: readonly string[]): Promise<PortalBlatt[]> {
  const session = await openTeilnehmerakte(coachingId)
  try {
    return await Promise.all(blaetter.map((blatt) => getBlattWithToken(blatt, session.token)))
  } finally {
    void logoutToken(session.token)
  }
}
